import { capturePostHogEvent } from 'src/providers/posthog/posthogConfig';
import type { OptionalOrderParams } from '@velocity-exchange/sdk';
import {
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	BASE_PRECISION_EXP,
	MarketType,
	BN,
} from '@velocity-exchange/sdk';
import { UIMarket } from '@velocity-exchange/common';

type TrackedOrder = {
	submissionTime: number;
	orderParams: OptionalOrderParams;
	orderMethod: 'swift' | 'legacy';
	userAccountKey: string;
	auctionVersion: number;
	hash?: string;
	oraclePrice?: BN;
};

export type OrderFilledChecker = (
	orderId: number,
	userAccountKey: string
) => boolean;

// @velocity-exchange/posthog-types still types this as 1 | 2; cast until the schema adds version 3+
const toPostHogAuctionVersion = (version?: number) =>
	version as 1 | 2 | undefined;

function serializeOrderParams(
	orderParams: OptionalOrderParams
): Record<string, unknown> {
	const serialized: Record<string, unknown> = {};

	let basePrecisionExp = BASE_PRECISION_EXP;
	if (
		orderParams.marketIndex !== undefined &&
		orderParams.marketType !== undefined
	) {
		try {
			if (orderParams.marketType === MarketType.PERP) {
				basePrecisionExp = BASE_PRECISION_EXP;
			} else {
				const spotMarket = UIMarket.spotMarkets[orderParams.marketIndex];
				basePrecisionExp = spotMarket?.precisionExp;
			}
		} catch {
			basePrecisionExp = BASE_PRECISION_EXP;
		}
	}

	for (const [key, value] of Object.entries(orderParams)) {
		if (BN.isBN(value)) {
			if (key === 'baseAssetAmount') {
				serialized[key] = BigNum.from(value, basePrecisionExp).print();
			} else if (
				key === 'price' ||
				key === 'triggerPrice' ||
				key === 'auctionStartPrice' ||
				key === 'auctionEndPrice'
			) {
				serialized[key] = BigNum.from(value, PRICE_PRECISION_EXP).print();
			} else if (key === 'maxTs') {
				serialized[key] = value.toNumber();
			} else {
				serialized[key] = BigNum.from(value, QUOTE_PRECISION_EXP).print();
			}
		} else {
			serialized[key] = value;
		}
	}

	return serialized;
}

class OrderFillTimeTrackerClass {
	private trackedOrders: Map<number, TrackedOrder> = new Map();
	private orderFilledChecker: OrderFilledChecker | undefined;

	setOrderFilledChecker(checker: OrderFilledChecker) {
		this.orderFilledChecker = checker;
	}

	trackOrderSubmission(
		orderId: number,
		submissionTime: number,
		orderParams: OptionalOrderParams,
		orderMethod: 'swift' | 'legacy',
		userAccountKey: string,
		auctionVersion: number,
		hash?: string,
		oraclePrice?: BN
	) {
		this.trackedOrders.set(orderId, {
			submissionTime,
			orderParams,
			orderMethod,
			userAccountKey,
			auctionVersion,
			hash,
			oraclePrice,
		});
	}

	trackOrderFill(
		orderId: number,
		fillTime: number,
		txSignature?: string,
		userAccountKey?: string,
		auctionVersion?: number
	) {
		const trackedOrder = this.trackedOrders.get(orderId);
		if (!trackedOrder) {
			return;
		}

		const fillTimeMs = fillTime - trackedOrder.submissionTime;

		capturePostHogEvent('trade_order_fill_time_recorded', {
			time_to_fill_ms: fillTimeMs,
			tx_signature: txSignature,
			order_id: orderId,
			user_account_key: userAccountKey,
			order_method: trackedOrder.orderMethod,
			order_params: serializeOrderParams(trackedOrder.orderParams),
			auction_version: toPostHogAuctionVersion(auctionVersion),
			hash: trackedOrder.hash,
			oracle_price: trackedOrder.oraclePrice
				? BigNum.from(trackedOrder.oraclePrice, PRICE_PRECISION_EXP).print()
				: undefined,
		});

		this.trackedOrders.delete(orderId);
	}

	trackOrderConfirmation(
		orderId: number,
		confirmationTime: number,
		txSignature?: string,
		userAccountKey?: string,
		auctionVersion?: number
	) {
		const trackedOrder = this.trackedOrders.get(orderId);
		if (!trackedOrder) {
			return;
		}

		const confirmTimeMs = confirmationTime - trackedOrder.submissionTime;

		capturePostHogEvent('trade_order_confirmation_time_recorded', {
			time_to_confirm_ms: confirmTimeMs,
			tx_signature: txSignature,
			order_id: orderId,
			user_account_key: userAccountKey ?? trackedOrder.userAccountKey,
			order_method: trackedOrder.orderMethod,
			order_params: serializeOrderParams(trackedOrder.orderParams),
			auction_version: toPostHogAuctionVersion(
				auctionVersion ?? trackedOrder.auctionVersion
			),
			hash: trackedOrder.hash,
			oracle_price: trackedOrder.oraclePrice
				? BigNum.from(trackedOrder.oraclePrice, PRICE_PRECISION_EXP).print()
				: undefined,
		});
	}

	cleanupOrder(orderId: number) {
		this.trackedOrders.delete(orderId);
	}

	cleanupOldOrders() {
		const now = Date.now();
		const oneMinute = 1 * 60 * 1000;

		for (const [orderId, trackedOrder] of this.trackedOrders.entries()) {
			if (now - trackedOrder.submissionTime > oneMinute) {
				const wasFilledInHistory =
					this.orderFilledChecker?.(orderId, trackedOrder.userAccountKey) ??
					false;

				if (wasFilledInHistory) {
					this.trackedOrders.delete(orderId);
					continue;
				}

				capturePostHogEvent('trade_order_fill_time_recorded', {
					time_to_fill_ms: -1,
					tx_signature: undefined,
					order_id: orderId,
					user_account_key: trackedOrder.userAccountKey,
					order_method: trackedOrder.orderMethod,
					order_params: serializeOrderParams(trackedOrder.orderParams),
					auction_version: toPostHogAuctionVersion(trackedOrder.auctionVersion),
					oracle_price: trackedOrder.oraclePrice
						? BigNum.from(trackedOrder.oraclePrice, PRICE_PRECISION_EXP).print()
						: undefined,
				});

				this.trackedOrders.delete(orderId);
			}
		}
	}
}

export const OrderFillTimeTracker = new OrderFillTimeTrackerClass();

if (typeof window !== 'undefined') {
	setInterval(() => {
		OrderFillTimeTracker.cleanupOldOrders();
	}, 60_000);
}
